import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../index";
import {
  Hull,
  ShipDetails,
  Crew,
  Weapon,
  AmmunitionStorage,
  DefensiveSystem,
} from "../../types/ship";
import { OptionalSystem } from "../../types/optionalSystems";
import { PowerPlant, PowerPlantType } from "../../types/powerPlants";
import { Drive, DriveType } from "../../types/drive";
import { Bridge, BridgeType } from "../../types/bridge";
import { Sensors } from "../../types/sensors";
import { Armor } from "../../types/ship";
import { StateroomConfig } from "../../types/ship";
import { ShipStats } from "../../types/ship";
import { CrewComposition } from "../../types/ship";
import {
  CrewMember,
  CrewRole,
  CrewMode,
  CrewOverrides,
} from "../../types/crew";
import { ShipBuilderState, SmallCraft } from "../../types/ship";
import { ComputerSelection, Computer } from "../../types/computer";
import { HullAndArmorStatsOutput } from "../../utils/calculations/hullAndArmorCalculations";
import { DriveStatsOutput } from "../../utils/calculations/driveCalculations";
import { getDriveSpec, validateDriveSpec } from "../../constants/drives";
import { POWER_PLANTS } from "../../constants/powerPlants";
import { calculatePowerPlantFuelConsumption } from "../../utils/calculations/fuelCalculations";
import { HULL_CONFIGURATIONS } from "../../constants/hull";
import { SoftwareSelection } from "../../types/software";
import type { WeaponMount } from "../../types/weapons";

// Start with 0 values to require user input

/**
 * Helper function to calculate power requirements for drives
 */
const calculatePowerRequirementsForDrives = (drives: Drive[], shipSize: number): number => {
  let totalPower = shipSize * 0.2; // Basic ship systems: 20% of hull tonnage
  
  drives.forEach(drive => {
    if (drive.type === DriveType.MANEUVER) {
      totalPower += shipSize * 0.1 * drive.rating; // Maneuver drive: 10% × thrust rating
    } else if (drive.type === DriveType.JUMP) {
      totalPower += shipSize * 0.1 * drive.rating; // Jump drive: 10% × jump rating
    }
    // Reaction drives don't require power from the power plant
  });
  
  return totalPower;
};

/**
 * Helper function to update power plant size based on drive requirements
 */
const updatePowerPlantForDrives = (state: ShipBuilderState) => {
  if (!state.powerPlant) return;
  
  const requiredPower = calculatePowerRequirementsForDrives(state.drives, state.shipSize);
  const plantSpec = POWER_PLANTS[state.powerPlant.type];
  
  if (!plantSpec) return;
  
  // Calculate required power plant size
  const requiredPowerPlantTons = Math.ceil(requiredPower / plantSpec.powerPerTon);
  
  // Only update if current power plant is smaller than required
  if (state.powerPlant.weight < requiredPowerPlantTons) {
    const newPower = requiredPowerPlantTons * plantSpec.powerPerTon;
    const newCost = requiredPowerPlantTons * plantSpec.costPerTon * 1000000;
    const newMaintenanceCost = requiredPowerPlantTons * plantSpec.maintenanceCost * 1000000;
    
    state.powerPlant = {
      ...state.powerPlant,
      weight: requiredPowerPlantTons,
      power: newPower,
      cost: newCost,
      maintenanceCost: newMaintenanceCost,
    };
  }
};

/**
 * Helper function to calculate fuel requirements for drives and power plant
 */
const calculateFuelRequirements = (drives: Drive[], powerPlant: PowerPlant | null, shipSize: number, jumpDriveReserveParsecs: number = 0): number => {
  let totalFuel = 0;
  
  // Jump drive fuel (10% of ship tonnage × jump rating × reserve parsecs)
  const jumpDrive = drives.find(d => d.type === DriveType.JUMP);
  if (jumpDrive) {
    // Use jumpDriveReserveParsecs if set, otherwise use jump drive rating for one jump
    const parsecs = jumpDriveReserveParsecs || jumpDrive.rating;
    totalFuel += shipSize * 0.1 * parsecs;
  }
  
  // Power plant fuel (10% of power plant size per month, minimum 1 ton)
  if (powerPlant) {
    totalFuel += calculatePowerPlantFuelConsumption(powerPlant, 4); // 4 weeks = 1 month
  }
  
  return Math.max(1, totalFuel); // Minimum 1 ton of fuel
};

/**
 * Helper function to update fuel tank size based on drive and power plant requirements
 */
const updateFuelTanksForDrives = (state: ShipBuilderState) => {
  const requiredFuel = calculateFuelRequirements(state.drives, state.powerPlant, state.shipSize, state.jumpDriveReserveParsecs);
  
  // Only update if current fuel tanks are smaller than required
  if ((state.fuelTanks || 0) < requiredFuel) {
    state.fuelTanks = requiredFuel;
  }
};

/**
 * Helper function to update fuel tank size when power plant changes
 */
const updateFuelTanksForPowerPlant = (state: ShipBuilderState) => {
  const requiredFuel = calculateFuelRequirements(state.drives, state.powerPlant, state.shipSize, state.jumpDriveReserveParsecs);
  
  // Only update if current fuel tanks are smaller than required
  if ((state.fuelTanks || 0) < requiredFuel) {
    state.fuelTanks = requiredFuel;
  }
};
const defaultHullConfig = HULL_CONFIGURATIONS[0];
// Remove default cost calculation - will be calculated when hull is actually configured
// Remove default bridge cost/weight calculations - will be calculated when bridge is actually configured

export const defaultSettings = {
  overrideTechLevel: false,
  overrideWeight: false,
  overrideSize: false,
  overridePower: false,
  overrideAllRestrictions: false,
};

/**
 * Initial state for the ship builder slice
 * Contains default values for all ship components and configurations
 */
export const initialState: ShipBuilderState = {
  currentStep: 0,
  shipSize: 0, // Start with 0 to require user input
  techLevel: 0, // Start with 0 to require user input
  hull: {
    size: 0, // Start with 0 to require user input
    type: "standard",
    configuration: defaultHullConfig,
    cost: 0, // Zero cost until hull is actually configured
    weight: 0, // Start with 0 to require user input
    hullPoints: 0, // Will be calculated when size is set
  },
  drives: [],
  powerPlant: {
    type: "fission" as PowerPlantType,
    cost: 0,
    weight: 0,
    power: 0,
    rating: 0,
    techLevel: 7,
    maintenanceCost: 0,
    description: "",
  },
  computer: {
    primary: null,
    backup: null,
  },
  sensors: null,
  armor: null,
  software: {
    installed: [
      { packageId: "manoeuvre", level: 0, installed: true },
      { packageId: "intellect", level: 0, installed: true },
      { packageId: "library", level: 0, installed: true },
    ],
    running: [
      { packageId: "manoeuvre", level: 0, running: true },
      { packageId: "intellect", level: 0, running: true },
      { packageId: "library", level: 0, running: true },
    ],
    expertSkills: {},
    translatorLanguages: {},
    databaseTopics: {},
    virtualCrewRoles: {},
    virtualGunnerRoles: {},
  },
  features: [],
  finalDetails: {
    name: "",
    class: "",
    owner: "",
    description: "",
    notes: "",
    registry: "",
    homePort: "",
    manufacturer: "",
    model: "",
    yearBuilt: 0,
    additionalAirlocks: 0,
    cargoHatches: 0,
    totalCost: 0,
    totalTons: 0,
    totalPower: 0,
    remainingTons: 0,
    image: "",
  },
  hullAndArmorStats: {
    totalCost: 0,
    baseHullCost: 0,
    modifiedHullCost: 0,
    optionsCost: 0,
    armorCost: 0,
    hullPoints: 0,
    totalConsumedVolume: 0,
    additionalHullVolume: 0,
    stealthVolume: 0,
    armorVolume: 0,
  },
  driveStats: {
    totalCost: 0,
    maneuverDriveCost: 0,
    jumpDriveCost: 0,
    reactionDriveCost: 0,
    totalConsumedVolume: 0,
    maneuverDriveVolume: 0,
    jumpDriveVolume: 0,
    reactionDriveVolume: 0,
  },
  selectedHull: null,
  selectedDrives: [],
  selectedPowerPlant: null,
  selectedBridge: null,
  selectedArmor: null,
  selectedSensors: null,
  selectedCargo: null,
  selectedFuel: null,
  selectedStaterooms: null,
  selectedCrew: null,
  selectedSoftware: null,
  crewComposition: {
    captain: 0,
    pilot: 0,
    astrogator: 0,
    engineer: 0,
    maintenance: 0,
    gunner: 0,
    steward: 0,
    administrator: 0,
    sensorOperator: 0,
    medic: 0,
    officer: 0,
    marines: 0,
  },
  stateroomConfig: {
    single: 0,
    double: 0,
    lowBerths: {
      count: 0,
      cost: 0,
      tons: 0,
      power: 0,
      capacity: 0,
      techLevel: 0,
      maintenanceCost: 0,
    },
    emergencyLowBerths: {
      count: 0,
      cost: 0,
      tons: 0,
      power: 0,
      capacity: 0,
      techLevel: 0,
      maintenanceCost: 0,
    },
    commonArea: 0,
    totalCost: 0,
    totalTons: 0,
    totalPower: 0,
    comfort: 0,
    warnings: [],
    autoAssignStaterooms: true,
    stateroomPercentage: 100,
  },
  passengerConfig: {
    high: 0,
    middle: 0,
    basic: 0,
    low: 0,
    cargoOverrides: {
      high: 1, // tons
      middle: 100, // kg
      basic: 10, // kg
      low: 10, // kg
    },
  },
  cargoSpace: 0,
  fuelTanks: 0,
  weaponSelections: [],
  weaponCounts: {},
  smallCraftCount: 0,
  smallCraft: [],
  passengerCounts: {},
  shipDetails: {
    name: "",
    class: "",
    owner: "",
    description: "",
    notes: "",
    registry: "",
    homePort: "",
    manufacturer: "",
    model: "",
    yearBuilt: 0,
    additionalAirlocks: 0,
    cargoHatches: 0,
    totalCost: 0,
    totalTons: 0,
    totalPower: 0,
    remainingTons: 0,
  },
  isDirty: false,
  shipStats: {
    totalCost: 0,
    totalWeight: 0,
    powerRequirements: 0,
    cargoSpace: 0,
    crewRequirements: 0,
    totalCrew: 0,
    totalPassengers: 0,
    bridgeOfficers: 0,
    engineers: 0,
    gunners: 0,
    stewards: 0,
    totalPower: 0,
    powerRequired: 0,
    size: 1000,
    techLevel: 12,
    availableCargo: 0,
    fuelRequired: 0,
    jumpRange: 0,
    maneuverRating: 0,
    totalCargo: 0,
    usedCargo: 0,
    monthlyCost: 0,
    crewComposition: {
      captain: 0,
      pilot: 0,
      astrogator: 0,
      engineer: 0,
      maintenance: 0,
      gunner: 0,
      steward: 0,
      administrator: 0,
      sensorOperator: 0,
      medic: 0,
      officer: 0,
      marines: 0,
    },
    drives: [],
    powerPlant: {
      type: "fission" as PowerPlantType,
      cost: 0,
      weight: 0,
      power: 0,
      rating: 0,
      techLevel: 7,
      maintenanceCost: 0,
      description: "",
    },
    bridge: {
      type: "STANDARD" as BridgeType,
      cost: 0, // Zero cost until bridge is actually configured
      weight: 0, // Zero weight until bridge is actually configured
      techLevel: 7,
      special: null,
      maintenanceCost: 0, // Zero maintenance until bridge is actually configured
      power: 0, // Zero power until bridge is actually configured
      sizeRange: {
        min: 0,
        max: Infinity,
      },
      description: "Standard bridge with all necessary controls and equipment",
      id: "standard",
    },
    armor: {
      type: "",
      cost: 0,
      weight: 0,
      power: 0,
      rating: 0,
      protection: "",
      techLevel: 7,
      special: null,
      maintenanceCost: 0,
    },
    sensors: {
      type: "",
      cost: 0,
      power: 0,
      rating: 0,
      tons: 0,
      name: "",
      description: "",
      capabilities: [],
      dm: 0,
      techLevel: 7,
    },
    weapons: [],
    weaponMounts: [],
    stateroomConfig: {
      single: 0,
      double: 0,
      lowBerths: {
        count: 0,
        cost: 0,
        tons: 0,
        power: 0,
        capacity: 0,
        techLevel: 0,
        maintenanceCost: 0,
      },
      emergencyLowBerths: {
        count: 0,
        cost: 0,
        tons: 0,
        power: 0,
        capacity: 0,
        techLevel: 0,
        maintenanceCost: 0,
      },
      commonArea: 0,
      totalCost: 0,
      totalTons: 0,
      totalPower: 0,
      comfort: 0,
      warnings: [],
    },
    fuelTanks: 0,
    hull: {
      type: "",
      weight: 0,
    },
  },
  validation: {
    errors: [],
    warnings: [],
  },
  crew: {
    captain: {
      role: CrewRole.CAPTAIN,
      skillLevel: 1,
      isOfficer: true,
      additionalRoles: [],
    },
    pilots: [],
    astrogators: [],
    engineers: [],
    maintenance: [],
    gunners: [],
    stewards: [],
    administrators: [],
    sensorOperators: [],
    medics: [],
    officers: [],
    drives: [],
    staterooms: [],
    software: {
      installed: [],
      running: [],
      expertSkills: {},
      translatorLanguages: {},
      databaseTopics: {},
      virtualCrewRoles: {},
      virtualGunnerRoles: {},
    },
    departments: {},
  },
  size: 1000,
  bridge: {
    type: "STANDARD" as BridgeType,
    cost: 0, // Zero cost until bridge is actually configured
    weight: 0, // Zero weight until bridge is actually configured
    techLevel: 7,
    special: null,
    maintenanceCost: 0, // Zero maintenance until bridge is actually configured
    power: 0, // Zero power until bridge is actually configured
    sizeRange: {
      min: 0,
      max: Infinity,
    },
    description: "Standard bridge with all necessary controls and equipment",
    id: "standard",
  },
  weapons: [] as Weapon[],
  optionalSystems: [],
  // DEPRECATED: Use stateroomConfig as the single source of truth for staterooms.
  staterooms: [],
  cargo: null,
  fuel: null,
  visitedSteps: [],
  weaponMounts: [],
  defensiveSystems: [],
  ammunitionStorage: [],
  powerPlantReserveHours: 672,
  jumpDriveReserveParsecs: 0,
  reactionDriveHours: 1,
  crewMode: "Commercial" as CrewMode,
  crewOverrides: {} as CrewOverrides,
  supplyUnits: 0,
  supplyTons: 0,
  settings: {
    ...defaultSettings,
    ...(typeof window !== 'undefined' && window.localStorage ? (JSON.parse(window.localStorage.getItem('shipBuilder') || '{}').settings || {}) : {})
  },
  lastRecommendedCrewByRole: {},
};

/**
 * Ship builder slice for managing ship construction state
 */
export const shipBuilderSlice = createSlice({
  name: "shipBuilder",
  initialState,
  reducers: {
    setLastRecommendedCrew: (state, action: PayloadAction<Record<string, number>>) => {
      state.lastRecommendedCrewByRole = action.payload || {};
    },
    clearLastRecommendedCrew: (state) => {
      state.lastRecommendedCrewByRole = {};
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
    },
    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
    },
    setHull: (state, action: PayloadAction<Hull>) => {
      state.hull = action.payload;
    },
    setDrives: (state, action: PayloadAction<Drive[]>) => {
      state.drives = action.payload;
    },
    setDrive: (state, action: PayloadAction<Drive>) => {
      const existingIndex = state.drives.findIndex(
        (d: Drive) => d.type === action.payload.type,
      );
      if (existingIndex !== -1) {
        state.drives[existingIndex] = action.payload;
      } else {
        state.drives.push(action.payload);
      }
    },
    setDriveWithValidation: (
      state,
      action: PayloadAction<{ 
        type: DriveType; 
        rating: number | "none"; 
        section?: number;
        role?: 'primary' | 'additional';
        driveId?: string;
      }>,
    ) => {
      try {
        const { type, rating, section, role = 'primary', driveId } = action.payload;
        if (process.env.NODE_ENV === 'development') {
      console.log("setDriveWithValidation called with:", { type, rating, section, role, driveId });
    }

        // Ensure drives array is initialized
        if (!state.drives) {
          state.drives = [];
        }
        
        // Handle existing drives based on context
        if (section !== undefined) {
          // For breakaway sections, remove drives of this type for this specific section
          state.drives = state.drives.filter((d) => !(d.type === type && d.section === section));
        } else {
          // For non-breakaway ships, handle multiple drives
          if (rating === "none") {
            // If removing a specific drive by ID
            if (driveId) {
              state.drives = state.drives.filter((d) => d.id !== driveId);
            } else {
              // If no driveId, remove all drives of this type (backward compatibility)
              state.drives = state.drives.filter((d) => d.type !== type);
            }
          } else {
            // If we're setting a specific rating
            if (driveId) {
              // Update existing drive by ID
              const existingDriveIndex = state.drives.findIndex((d) => d.id === driveId);
              if (existingDriveIndex !== -1) {
                state.drives[existingDriveIndex] = {
                  ...state.drives[existingDriveIndex],
                  rating: rating as number,
                  description: `${type === DriveType.MANEUVER ? 'Maneuver' : type === DriveType.JUMP ? 'Jump' : 'Reaction'}-${rating}`,
                };
              }
            } else {
              // Add new drive or replace primary drive
              if (role === 'primary') {
                // Remove existing primary drive of this type
                state.drives = state.drives.filter((d) => !(d.type === type && d.role === 'primary'));
              }
              
              // Generate unique ID for new drive
              const newDriveId = `${type}-${role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              
              // Add new drive
              state.drives.push({
                type,
                rating: rating as number,
                techLevel: 7, // Default tech level, should be calculated based on available options
                description: `${type === DriveType.MANEUVER ? 'Maneuver' : type === DriveType.JUMP ? 'Jump' : 'Reaction'}-${rating}`,
                isPrimary: role === 'primary',
                id: newDriveId,
                role,
              });
            }
          }
        }
        if (process.env.NODE_ENV === 'development') {
          console.log("Updated drives array:", state.drives);
        }

        // If rating is "none", don't add any drive (no drive installed)
        if (rating === "none") {
          if (process.env.NODE_ENV === 'development') {
          console.log("Rating is 'none', not adding drive");
        }
          
          // Auto-update power plant and fuel tank sizes when drives are removed
          if (section === undefined) { // Only for non-breakaway ships
            updatePowerPlantForDrives(state);
            updateFuelTanksForDrives(state);
          }
          
          return;
        }

        // Handle numeric rating (including 0)
        const numericRating =
          typeof rating === "number" ? rating : parseInt(rating as string);
        if (process.env.NODE_ENV === 'development') {
          console.log("Parsed numeric rating:", numericRating);
        }
        
        if (isNaN(numericRating)) {
          if (process.env.NODE_ENV === 'development') {
            console.log("Invalid numeric rating:", rating);
          }
          return;
        }

        console.log("Looking for drive spec for:", { type, numericRating });
        const spec = getDriveSpec(type, numericRating);
        console.log("Drive spec found:", spec);
        
        const isValid = validateDriveSpec(type, numericRating);
        console.log("Drive validation result:", isValid);
        
        if (spec && isValid) {
          const drive: Drive = {
            type,
            rating: numericRating,
            techLevel: spec.techLevel,
            description:
              type === DriveType.JUMP
                ? `Jump-${numericRating} drive allowing FTL travel of ${numericRating} parsecs`
                : type === DriveType.MANEUVER
                  ? `Maneuver drive providing ${numericRating}G of thrust`
                  : `Reaction drive providing ${numericRating}G of thrust`,
            isPrimary: true, // Default to primary drive when added through UI
            ...(section !== undefined && { section }), // Add section if specified
          };
          console.log("Adding drive to state:", drive);
          state.drives.push(drive);
          console.log("Drive added successfully. Current drives:", state.drives);
        console.log("Drive array length:", state.drives.length);
        console.log("Drive validation for power step:", state.drives && state.drives.length > 0);

        // Auto-update power plant and fuel tank sizes when drives change
        if (section === undefined) { // Only for non-breakaway ships
          updatePowerPlantForDrives(state);
          
          // Auto-set jump drive reserve to jump drive rating if it's currently 0
          if (type === DriveType.JUMP && numericRating > 0 && state.jumpDriveReserveParsecs === 0) {
            state.jumpDriveReserveParsecs = numericRating;
          }
          
          updateFuelTanksForDrives(state);
        }
        } else {
          console.log("Drive validation failed - spec:", spec, "valid:", isValid);
        }
      } catch (error) {
        console.error("Error in setDriveWithValidation:", error);
      }
    },
    setPowerPlant: (state, action: PayloadAction<PowerPlant>) => {
      state.powerPlant = action.payload;
      
      // Auto-update fuel tank size when power plant changes
      updateFuelTanksForPowerPlant(state);
    },
    setSensors: (state, action: PayloadAction<Sensors | null>) => {
      state.sensors = action.payload;
    },
    setArmor: (state, action: PayloadAction<Armor | null>) => {
      state.armor = action.payload;
    },
    setBridge: (state, action: PayloadAction<Bridge>) => {
      state.bridge = action.payload;
    },
    setComputer: (state, action: PayloadAction<ComputerSelection>) => {
      state.computer = action.payload;
    },
    setPrimaryComputer: (state, action: PayloadAction<Computer | null>) => {
      if (!state.computer) {
        state.computer = { primary: null, backup: null };
      }
      state.computer.primary = action.payload;
    },
    setBackupComputer: (state, action: PayloadAction<Computer | null>) => {
      if (!state.computer) {
        state.computer = { primary: null, backup: null };
      }
      state.computer.backup = action.payload;
    },
    setCrew: (state, action: PayloadAction<Crew>) => {
      state.crew = action.payload;
    },
    setStaterooms: (state, action: PayloadAction<StateroomConfig>) => {
      state.stateroomConfig = action.payload;
    },
    setFuel: (state, action: PayloadAction<number>) => {
      state.fuelTanks = action.payload;
    },
    setShipDetails: (state, action: PayloadAction<ShipDetails>) => {
      state.finalDetails = action.payload;
    },
    updateShipStats: (state, action: PayloadAction<ShipStats>) => {
      state.shipStats = action.payload;
    },
    setCargoSpace: (state, action: PayloadAction<number>) => {
      state.cargoSpace = action.payload;
    },
    setCrewComposition: (state, action: PayloadAction<CrewComposition>) => {
      // Always set all roles, defaulting to 0 if not provided
      const input = action.payload || {};
      state.crewComposition = {
        captain: input.captain ?? 0,
        pilot: input.pilot ?? 0,
        astrogator: input.astrogator ?? 0,
        engineer: input.engineer ?? 0,
        maintenance: input.maintenance ?? 0,
        gunner: input.gunner ?? 0,
        steward: input.steward ?? 0,
        administrator: input.administrator ?? 0,
        sensorOperator: input.sensorOperator ?? 0,
        medic: input.medic ?? 0,
        officer: input.officer ?? 0,
        marines: input.marines ?? 0,
      };
    },
    setFuelTanks: (state, action: PayloadAction<number>) => {
      state.fuelTanks = action.payload;
    },
    setPowerPlantReserveHours: (state, action: PayloadAction<number>) => {
      state.powerPlantReserveHours = action.payload;
    },
    setJumpDriveReserveParsecs: (state, action: PayloadAction<number>) => {
      state.jumpDriveReserveParsecs = action.payload;
      
      // Auto-update fuel tank size when jump drive reserve changes
      updateFuelTanksForDrives(state);
    },
    setReactionDriveHours: (state, action: PayloadAction<number>) => {
      state.reactionDriveHours = action.payload;
    },
    setSoftware: (state, action: PayloadAction<SoftwareSelection>) => {
      state.software = action.payload;
    },
    setStateroomConfig: (state, action: PayloadAction<StateroomConfig>) => {
      state.stateroomConfig = action.payload;
    },
    setStateroomAutoAssign: (state, action: PayloadAction<boolean>) => {
      if (state.stateroomConfig) {
        state.stateroomConfig.autoAssignStaterooms = action.payload;
      }
    },
    setStateroomPercentage: (state, action: PayloadAction<number>) => {
      if (state.stateroomConfig) {
        state.stateroomConfig.stateroomPercentage = action.payload;
      }
    },
    setWeapons: (state, action: PayloadAction<Weapon[]>) => {
      state.weapons = action.payload;
    },
    setCargo: (state, action: PayloadAction<number>) => {
      state.cargoSpace = action.payload;
    },
    setShipName: (state, action: PayloadAction<string>) => {
      state.finalDetails.name = action.payload;
    },
    setShipClass: (state, action: PayloadAction<string>) => {
      state.finalDetails.class = action.payload;
    },
    setShipImage: (state, action: PayloadAction<string>) => {
      if (state.finalDetails) {
        state.finalDetails.image = action.payload;
      } else {
        // Initialize finalDetails if it doesn't exist
        state.finalDetails = {
          name: "",
          class: "",
          owner: "",
          description: "",
          notes: "",
          registry: "",
          homePort: "",
          manufacturer: "",
          model: "",
          yearBuilt: 0,
          additionalAirlocks: 0,
          cargoHatches: 0,
          totalCost: 0,
          totalTons: 0,
          totalPower: 0,
          remainingTons: 0,
          image: "",
        };
        state.finalDetails.image = action.payload;
      }
    },
    addDeckPlan: (state, action: PayloadAction<{ name: string; image: string; description?: string }>) => {
      if (!state.finalDetails.deckplans) {
        state.finalDetails.deckplans = [];
      }
      const newDeckPlan = {
        id: Date.now().toString(),
        name: action.payload.name,
        image: action.payload.image,
        description: action.payload.description,
      };
      state.finalDetails.deckplans.push(newDeckPlan);
    },
    removeDeckPlan: (state, action: PayloadAction<string>) => {
      if (state.finalDetails.deckplans) {
        state.finalDetails.deckplans = state.finalDetails.deckplans.filter(
          (deckplan) => deckplan.id !== action.payload
        );
      }
    },
    updateDeckPlan: (state, action: PayloadAction<{ id: string; name?: string; description?: string }>) => {
      if (state.finalDetails.deckplans) {
        const deckplan = state.finalDetails.deckplans.find(d => d.id === action.payload.id);
        if (deckplan) {
          if (action.payload.name !== undefined) deckplan.name = action.payload.name;
          if (action.payload.description !== undefined) deckplan.description = action.payload.description;
        }
      }
    },
    setShipDescription: (state, action: PayloadAction<string>) => {
      state.finalDetails.description = action.payload;
    },
    addOptionalSystem: (state, action: PayloadAction<OptionalSystem>) => {
      state.optionalSystems.push(action.payload);
    },
    removeOptionalSystem: (state, action: PayloadAction<string>) => {
      state.optionalSystems = state.optionalSystems.filter(
        (system: OptionalSystem) => system.id !== action.payload,
      );
    },
    addWeapon: (state, action: PayloadAction<Weapon>) => {
      if (!state.weapons) state.weapons = [];
      (state.weapons as Weapon[]).push(action.payload);
    },
    removeWeapon: (state, action: PayloadAction<string>) => {
      if (state.weapons) {
        state.weapons = (state.weapons as Weapon[]).filter(
          (w: Weapon) => w.type !== action.payload,
        );
      }
    },
    resetShip: (state) => {
      return { ...initialState, lastRecommendedCrewByRole: {} } as any;
    },
    addCrewMember: (state, action: PayloadAction<CrewMember>) => {
      if (!state.crew) {
        state.crew = {} as Crew;
      }
      // Add crew member logic here
    },
    removeCrewMember: (state, action: PayloadAction<string>) => {
      if (state.crew) {
        // Remove crew member logic here
      }
    },
    updateCrewMember: (state, action: PayloadAction<CrewMember>) => {
      // Update crew member logic here
    },
    updateCrewAssignment: (
      state,
      action: PayloadAction<{
        role: string;
        skillLevel: number;
        count: number;
      }>
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { role, skillLevel, count } = action.payload;
      // This would need to be implemented based on how crew assignments are stored
      // For now, we'll just update the crew composition
      if (state.crewComposition[role as keyof CrewComposition]) {
        // Update the crew composition for the specific role and skill level
        // This is a placeholder implementation
      }
    },
    toggleOfficer: (state, action: PayloadAction<string>) => {
      // Toggle officer logic here
    },
    setHullConfiguration: (state, action: PayloadAction<Partial<Hull>>) => {
      if (state.hull) {
        state.hull = { ...state.hull, ...action.payload };
      }
    },
    setHullOption: (state, action: PayloadAction<Partial<Hull>>) => {
      if (state.hull) {
        state.hull = { ...state.hull, ...action.payload };
      }
    },
    setSpecializedHull: (state, action: PayloadAction<Partial<Hull>>) => {
      if (state.hull) {
        state.hull = { ...state.hull, ...action.payload };
      }
    },
    setAdditionalHull: (state, action: PayloadAction<Partial<Hull>>) => {
      if (state.hull) {
        state.hull = { ...state.hull, ...action.payload };
        
        // If breakaway hulls are selected, clear the old drives array to prevent interference
        if (action.payload.additionalType?.type === 'breakaway') {
          state.drives = [];
          if (process.env.NODE_ENV === 'development') {
            console.log('Cleared old drives array for breakaway hulls');
          }
        }
      }
    },
    markStepVisited: (state, action: PayloadAction<number>) => {
      if (!state.visitedSteps) {
        state.visitedSteps = [];
      }
      if (!state.visitedSteps.includes(action.payload)) {
        state.visitedSteps.push(action.payload);
      }
    },
    setHullAndArmorStats: (
      state,
      action: PayloadAction<HullAndArmorStatsOutput>,
    ) => {
      state.hullAndArmorStats = action.payload;
    },
    setDriveStats: (state, action: PayloadAction<DriveStatsOutput>) => {
      state.driveStats = action.payload;
    },
    setSoftwareSelection: (state, action: PayloadAction<SoftwareSelection>) => {
      state.software = action.payload;
    },
    installSoftware: (
      state,
      action: PayloadAction<{ packageId: string; level: number }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      const { packageId, level } = action.payload;
      const instanceId = `${packageId}_${level}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      state.software.installed.push({ packageId, level, installed: true, instanceId });
    },
    uninstallSoftware: (
      state,
      action: PayloadAction<{ packageId: string; level: number; instanceId?: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      const { packageId, level, instanceId } = action.payload;
      
      if (instanceId) {
        // Remove specific instance
        const existingIndex = state.software.installed.findIndex(
          (s) => s.packageId === packageId && s.level === level && s.instanceId === instanceId,
        );
        if (existingIndex >= 0) {
          state.software.installed.splice(existingIndex, 1);
        }
        
        // Also stop running if it was running
        const runningIndex = state.software.running.findIndex(
          (s) => s.packageId === packageId && s.level === level && s.instanceId === instanceId,
        );
        if (runningIndex >= 0) {
          state.software.running.splice(runningIndex, 1);
        }
        
        // Clean up configuration data for this instance
        if (state.software.expertSkills && state.software.expertSkills[instanceId]) {
          delete state.software.expertSkills[instanceId];
        }
        if (state.software.translatorLanguages && state.software.translatorLanguages[instanceId]) {
          delete state.software.translatorLanguages[instanceId];
        }
        if (state.software.databaseTopics && state.software.databaseTopics[instanceId]) {
          delete state.software.databaseTopics[instanceId];
        }
        if (state.software.virtualCrewRoles && state.software.virtualCrewRoles[instanceId]) {
          delete state.software.virtualCrewRoles[instanceId];
        }
        if (state.software.virtualGunnerRoles && state.software.virtualGunnerRoles[instanceId]) {
          delete state.software.virtualGunnerRoles[instanceId];
        }
      } else {
        // Remove all instances of this package/level
        state.software.installed = state.software.installed.filter(
          (s) => !(s.packageId === packageId && s.level === level),
        );
        state.software.running = state.software.running.filter(
          (s) => !(s.packageId === packageId && s.level === level),
        );
      }
    },
    setSoftwareLevel: (
      state,
      action: PayloadAction<{ packageId: string; level: number }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      const { packageId, level } = action.payload;
      const existingIndex = state.software.installed.findIndex(
        (s) => s.packageId === packageId,
      );
      if (existingIndex >= 0) {
        state.software.installed[existingIndex].level = level;
      } else {
        state.software.installed.push({ packageId, level, installed: true });
      }
    },
    startSoftware: (
      state,
      action: PayloadAction<{ packageId: string; level: number; instanceId?: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      const { packageId, level, instanceId } = action.payload;
      const existingIndex = state.software.running.findIndex(
        (s) => s.packageId === packageId && s.level === level && s.instanceId === instanceId,
      );
      if (existingIndex >= 0) {
        state.software.running[existingIndex].running = true;
      } else {
        state.software.running.push({ packageId, level, running: true, instanceId });
      }
    },
    stopSoftware: (
      state,
      action: PayloadAction<{ packageId: string; level: number; instanceId?: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      const { packageId, level, instanceId } = action.payload;
      const existingIndex = state.software.running.findIndex(
        (s) => s.packageId === packageId && s.level === level && s.instanceId === instanceId,
      );
      if (existingIndex >= 0) {
        state.software.running[existingIndex].running = false;
      }
    },
    setExpertSkill: (
      state,
      action: PayloadAction<{ instanceId: string; skill: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      if (!state.software.expertSkills) {
        state.software.expertSkills = {};
      }
      state.software.expertSkills[action.payload.instanceId] = action.payload.skill;
    },
    setTranslatorLanguage: (
      state,
      action: PayloadAction<{ instanceId: string; language: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      if (!state.software.translatorLanguages) {
        state.software.translatorLanguages = {};
      }
      state.software.translatorLanguages[action.payload.instanceId] = action.payload.language;
    },
    setDatabaseTopic: (
      state,
      action: PayloadAction<{ instanceId: string; topic: string }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      if (!state.software.databaseTopics) {
        state.software.databaseTopics = {};
      }
      state.software.databaseTopics[action.payload.instanceId] = action.payload.topic;
    },
    setVirtualCrewRoles: (
      state,
      action: PayloadAction<{ instanceId: string; roles: string[] }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      if (!state.software.virtualCrewRoles) {
        state.software.virtualCrewRoles = {};
      }
      state.software.virtualCrewRoles[action.payload.instanceId] = action.payload.roles;
    },
    setVirtualGunnerRoles: (
      state,
      action: PayloadAction<{ instanceId: string; roles: string[] }>,
    ) => {
      if (!state.software) {
        state.software = { installed: [], running: [], expertSkills: {}, translatorLanguages: {}, databaseTopics: {}, virtualCrewRoles: {}, virtualGunnerRoles: {} };
      }
      if (!state.software.virtualGunnerRoles) {
        state.software.virtualGunnerRoles = {};
      }
      state.software.virtualGunnerRoles[action.payload.instanceId] = action.payload.roles;
    },
    addWeaponMount: (state, action: PayloadAction<WeaponMount>) => {
      if (!state.weaponMounts) {
        state.weaponMounts = [];
      }
      state.weaponMounts.push(action.payload);
    },
    removeWeaponMount: (state, action: PayloadAction<number>) => {
      // Remove by index for now (can be changed to id if needed)
      if (!state.weaponMounts) {
        state.weaponMounts = [];
        return;
      }
      state.weaponMounts.splice(action.payload, 1);
    },
    updateWeaponMount: (
      state,
      action: PayloadAction<{ index: number; mount: WeaponMount }>,
    ) => {
      if (!state.weaponMounts) {
        state.weaponMounts = [];
        return;
      }
      state.weaponMounts[action.payload.index] = action.payload.mount;
    },
    addDefensiveSystem: (state, action: PayloadAction<DefensiveSystem>) => {
      if (!state.defensiveSystems) {
        state.defensiveSystems = [];
      }
      state.defensiveSystems.push(action.payload);
    },
    removeDefensiveSystem: (state, action: PayloadAction<number>) => {
      if (!state.defensiveSystems) {
        state.defensiveSystems = [];
        return;
      }
      state.defensiveSystems.splice(action.payload, 1);
    },
    updateDefensiveSystem: (
      state,
      action: PayloadAction<{ index: number; system: DefensiveSystem }>,
    ) => {
      if (!state.defensiveSystems) {
        state.defensiveSystems = [];
        return;
      }
      state.defensiveSystems[action.payload.index] = action.payload.system;
    },
    addAmmunition: (state, action: PayloadAction<AmmunitionStorage>) => {
      if (!state.ammunitionStorage) {
        state.ammunitionStorage = [];
      }
      state.ammunitionStorage.push(action.payload);
    },
    removeAmmunition: (state, action: PayloadAction<string>) => {
      if (!state.ammunitionStorage) {
        state.ammunitionStorage = [];
        return;
      }
      state.ammunitionStorage = state.ammunitionStorage.filter(
        (ammo) => ammo.id !== action.payload,
      );
    },
    updateAmmunitionQuantity: (
      state,
      action: PayloadAction<{ id: string; quantity: number }>,
    ) => {
      if (!state.ammunitionStorage) {
        state.ammunitionStorage = [];
        return;
      }
      const ammoIndex = state.ammunitionStorage.findIndex(
        (ammo) => ammo.id === action.payload.id,
      );
      if (ammoIndex !== -1) {
        state.ammunitionStorage[ammoIndex].quantity = action.payload.quantity;
      }
    },
    setCrewMode: (state, action: PayloadAction<CrewMode>) => {
      state.crewMode = action.payload;
    },
    setCrewOverrides: (state, action: PayloadAction<CrewOverrides>) => {
      state.crewOverrides = action.payload;
    },
    addSmallCraft: (state, action: PayloadAction<SmallCraft>) => {
      state.smallCraft.push(action.payload);
      state.smallCraftCount = state.smallCraft.length;
    },
    removeSmallCraft: (state, action: PayloadAction<string>) => {
      state.smallCraft = state.smallCraft.filter(craft => craft.id !== action.payload);
      state.smallCraftCount = state.smallCraft.length;
    },
    updateSmallCraft: (state, action: PayloadAction<SmallCraft>) => {
      const index = state.smallCraft.findIndex(craft => craft.id === action.payload.id);
      if (index !== -1) {
        state.smallCraft[index] = action.payload;
      }
    },
    setSmallCraft: (state, action: PayloadAction<SmallCraft[]>) => {
      state.smallCraft = action.payload;
      state.smallCraftCount = action.payload.length;
    },

    /**
     * Set the number of passengers for a given passage type
     * @param state
     * @param action - { type: 'high' | 'middle' | 'basic' | 'low', count: number }
     */
    setPassengerCount(
      state,
      action: PayloadAction<{
        type: keyof typeof state.passengerConfig;
        count: number;
      }>,
    ) {
      if (!state.passengerConfig) {
        state.passengerConfig = {
          high: 0,
          middle: 0,
          basic: 0,
          low: 0,
          cargoOverrides: { high: 1, middle: 100, basic: 10, low: 10 },
        };
      }
      const { type, count } = action.payload;
      if (["high", "middle", "basic", "low"].includes(type)) {
        state.passengerConfig[type as "high" | "middle" | "basic" | "low"] =
          count;
      }
    },

    /**
     * Set the cargo override for a given passage type
     * @param state
     * @param action - { type: 'high' | 'middle' | 'basic' | 'low', value: number }
     */
    setPassengerCargoOverride(
      state,
      action: PayloadAction<{
        type: keyof typeof state.passengerConfig.cargoOverrides;
        value: number;
      }>,
    ) {
      if (!state.passengerConfig) {
        state.passengerConfig = {
          high: 0,
          middle: 0,
          basic: 0,
          low: 0,
          cargoOverrides: { high: 1, middle: 100, basic: 10, low: 10 },
        };
      }
      if (!state.passengerConfig.cargoOverrides) {
        state.passengerConfig.cargoOverrides = {
          high: 1,
          middle: 100,
          basic: 10,
          low: 10,
        };
      }
      const { type, value } = action.payload;
      if (["high", "middle", "basic", "low"].includes(type)) {
        state.passengerConfig.cargoOverrides[
          type as "high" | "middle" | "basic" | "low"
        ] = value;
      }
    },
    setSupplyUnits(state, action) {
      state.supplyUnits = action.payload;
      state.supplyTons = action.payload / 100;
    },
    setSupplyTons(state, action) {
      state.supplyTons = action.payload;
      state.supplyUnits = action.payload * 100;
    },
    toggleOverrideTechLevel(state) {
      state.settings.overrideTechLevel = !state.settings.overrideTechLevel;
    },
    toggleOverrideWeight(state) {
      state.settings.overrideWeight = !state.settings.overrideWeight;
    },
    toggleOverrideSize(state) {
      state.settings.overrideSize = !state.settings.overrideSize;
    },
    toggleOverridePower(state) {
      state.settings.overridePower = !state.settings.overridePower;
    },
    toggleOverrideAllRestrictions(state) {
      state.settings.overrideAllRestrictions = !state.settings.overrideAllRestrictions;
      // If all restrictions are overridden, set all others to true
      if (state.settings.overrideAllRestrictions) {
        state.settings.overrideTechLevel = true;
        state.settings.overrideWeight = true;
        state.settings.overrideSize = true;
        state.settings.overridePower = true;
      }
    },

    loadShipData: (state, action: PayloadAction<ShipBuilderState>) => {
      // Merge the loaded data with current state, preserving some current settings
      const currentSettings = state.settings;
      const currentVisitedSteps = state.visitedSteps;
      
      // Replace the entire state with loaded data
      Object.assign(state, action.payload);
      
      // Preserve current settings and visited steps
      state.settings = currentSettings;
      state.visitedSteps = currentVisitedSteps;
    },

    // Load full application state (for comprehensive save/load)
    loadFullState: (state, action: PayloadAction<Partial<RootState>>) => {
      // Load shipBuilder state
      if (action.payload.shipBuilder) {
        const currentSettings = state.settings;
        const currentVisitedSteps = state.visitedSteps;
        
        Object.assign(state, action.payload.shipBuilder);
        
        // Preserve current settings and visited steps
        state.settings = currentSettings;
        state.visitedSteps = currentVisitedSteps;
      }
    },

    // Clear all breakaway hull state
    clearBreakawayState: (state) => {
      // Clear drives array if it was set up for breakaway hulls
      state.drives = [];
      
      // Clear armor configuration when deselecting breakaway hulls
      if (state.hull) {
        state.hull.armor = undefined;
      }
      
      console.log('Cleared breakaway state from shipBuilderSlice');
    },
    
    // Multiple drives management actions
    addDrive: (state, action: PayloadAction<{ type: DriveType; rating: number; role?: 'primary' | 'additional' }>) => {
      const { type, rating, role = 'additional' } = action.payload;
      
      if (!state.drives) {
        state.drives = [];
      }
      
      // Generate unique ID for new drive
      const newDriveId = `${type}-${role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      console.log(`Adding new drive:`, { type, rating, role, id: newDriveId });
      console.log(`Current drives before adding:`, state.drives.map(d => ({ id: d.id, type: d.type, rating: d.rating, role: d.role })));
      
      // Add new drive
      state.drives.push({
        type,
        rating,
        techLevel: 7, // Default tech level, should be calculated based on available options
        description: `${type === DriveType.MANEUVER ? 'Maneuver' : type === DriveType.JUMP ? 'Jump' : 'Reaction'}-${rating}`,
        isPrimary: role === 'primary',
        id: newDriveId,
        role,
      });
      
      console.log(`Drives after adding:`, state.drives.map(d => ({ id: d.id, type: d.type, rating: d.rating, role: d.role })));
      
      // Auto-set jump drive reserve to jump drive rating if it's currently 0 and this is a primary jump drive
      if (type === DriveType.JUMP && rating > 0 && role === 'primary' && state.jumpDriveReserveParsecs === 0) {
        state.jumpDriveReserveParsecs = rating;
        console.log(`Auto-set jump drive reserve to ${rating} parsecs for new primary jump drive`);
      }
    },
    
    removeDrive: (state, action: PayloadAction<{ driveId: string }>) => {
      const { driveId } = action.payload;
      if (state.drives) {
        console.log(`Removing drive with ID: ${driveId}`);
        console.log(`Current drives before removal:`, state.drives.map(d => ({ id: d.id, type: d.type, rating: d.rating })));
        state.drives = state.drives.filter((d) => d.id !== driveId);
        console.log(`Drives after removal:`, state.drives.map(d => ({ id: d.id, type: d.type, rating: d.rating })));
      }
    },
    
    updateDriveRole: (state, action: PayloadAction<{ driveId: string; role: 'primary' | 'additional' }>) => {
      const { driveId, role } = action.payload;
      if (state.drives) {
        const driveIndex = state.drives.findIndex((d) => d.id === driveId);
        if (driveIndex !== -1) {
          // If setting as primary, remove primary status from other drives of same type
          if (role === 'primary') {
            const driveType = state.drives[driveIndex].type;
            state.drives = state.drives.map((d) => 
              d.type === driveType && d.id !== driveId 
                ? { ...d, role: 'additional', isPrimary: false }
                : d
            );
          }
          
          // Update the drive
          state.drives[driveIndex] = {
            ...state.drives[driveIndex],
            role,
            isPrimary: role === 'primary',
          };
        }
      }
    },
  },
});

export const {
  setCurrentStep,
  setShipSize,
  setTechLevel,
  setHull,
  setDrives,
  setDrive,
  setDriveWithValidation,
  setPowerPlant,
  setSensors,
  setArmor,
  setBridge,
  setComputer,
  setPrimaryComputer,
  setBackupComputer,
  setCrew,
  setStaterooms,
  setFuel,
  setShipDetails,
  updateShipStats,
  setCargoSpace,
  setCrewComposition,
  setFuelTanks,
  setPowerPlantReserveHours,
  setJumpDriveReserveParsecs,
  setReactionDriveHours,
  setSoftware,
  setStateroomConfig,
  setStateroomAutoAssign,
  setStateroomPercentage,
  setWeapons,
  setCargo,
  setShipName,
  setShipClass,
  setShipImage,
  addDeckPlan,
  removeDeckPlan,
  updateDeckPlan,
  setShipDescription,
  addOptionalSystem,
  removeOptionalSystem,
  addWeapon,
  removeWeapon,
  resetShip,
  addCrewMember,
  removeCrewMember,
  updateCrewMember,
  updateCrewAssignment,
  toggleOfficer,
  setHullConfiguration,
  setHullOption,
  setSpecializedHull,
  setAdditionalHull,
  markStepVisited,
  setHullAndArmorStats,
  setDriveStats,
  setSoftwareSelection,
  installSoftware,
  uninstallSoftware,
  setSoftwareLevel,
  startSoftware,
  stopSoftware,
  setExpertSkill,
  setTranslatorLanguage,
  setDatabaseTopic,
  setVirtualCrewRoles,
  setVirtualGunnerRoles,
  addWeaponMount,
  removeWeaponMount,
  updateWeaponMount,
  addDefensiveSystem,
  removeDefensiveSystem,
  updateDefensiveSystem,
  addAmmunition,
  removeAmmunition,
  updateAmmunitionQuantity,
  setCrewMode,
  setCrewOverrides,
  setLastRecommendedCrew,
  clearLastRecommendedCrew,
  addSmallCraft,
  removeSmallCraft,
  updateSmallCraft,
  setSmallCraft,
  setPassengerCount,
  setPassengerCargoOverride,
  setSupplyUnits,
  setSupplyTons,
  toggleOverrideTechLevel,
  toggleOverrideWeight,
  toggleOverrideSize,
  toggleOverridePower,
  toggleOverrideAllRestrictions,
  loadShipData,
  loadFullState,
  clearBreakawayState,
  addDrive,
  removeDrive,
  updateDriveRole,
} = shipBuilderSlice.actions;

export default shipBuilderSlice.reducer;

// Selectors
export const selectShipBuilder = (state: RootState) => state.shipBuilder;
export const selectCurrentStep = (state: RootState) =>
  state.shipBuilder.currentStep;
export const selectShipSize = (state: RootState) => state.shipBuilder.shipSize;
export const selectTechLevel = (state: RootState) =>
  state.shipBuilder.techLevel;
export const selectHull = (state: RootState) => state.shipBuilder.hull;
export const selectDrives = (state: RootState) => state.shipBuilder.drives;
export const selectPowerPlant = (state: RootState) =>
  state.shipBuilder.powerPlant;
export const selectSensors = (state: RootState) => state.shipBuilder.sensors;
export const selectArmor = (state: RootState) => state.shipBuilder.armor;
export const selectBridge = (state: RootState) => state.shipBuilder.bridge;
export const selectCrew = (state: RootState) => state.shipBuilder.crew;
export const selectStaterooms = (state: RootState) =>
  state.shipBuilder.stateroomConfig;
export const selectFuel = (state: RootState) => state.shipBuilder.fuel;
export const selectShipDetails = (state: RootState) =>
  state.shipBuilder.finalDetails;
export const selectShipStats = (state: RootState) =>
  state.shipBuilder.shipStats;
export const selectVisitedSteps = (state: RootState) =>
  state.shipBuilder.visitedSteps;
export const selectComputer = (state: RootState) => state.shipBuilder.computer;
